import React from 'react';
import {
  HStack, Box, Text, Tooltip, TooltipContent, TooltipText, Pressable,
} from '@gluestack-ui/themed';
import type { LevelEngagement } from '@/common/api/models/levelEngagement';

export type TaskState = 'done' | 'started' | 'todo';

interface TaskBoxesProps {
  /** Map of level number (as string) to level engagement data */
  levelProgress: Record<string, LevelEngagement>;
  /** Compact mode shows smaller boxes without level labels */
  compact?: boolean;
  /** Callback when a task box is clicked */
  onTaskPress?: (taskId: string) => void;
}

const STATE_COLORS: Record<TaskState, string> = {
  done: '$success200', // Subtle green for completed
  started: '$textHint', // Grey for in progress
  todo: '$white', // White for not started (with grey border)
};

const STATE_LABELS: Record<TaskState, string> = {
  done: 'Completed',
  started: 'In Progress',
  todo: 'Not Started',
};

/**
 * TaskBoxes - Visualizes task completion status grouped by level.
 *
 * Renders colored boxes for each task (all with grey border):
 * - Subtle green (done): Task completed
 * - Grey (started): Task in progress
 * - White (todo): Task not started
 *
 * Example output:
 * L1: [■][■][■]  L2: [■][■][□]  L3: [□][□]
 */
const TaskBoxes: React.FC<TaskBoxesProps> = ({ levelProgress, compact = false, onTaskPress }) => {
  // Sort levels numerically
  const sortedLevels = Object.keys(levelProgress).sort((a, b) => Number(a) - Number(b));

  if (sortedLevels.length === 0) {
    return (
      <Text size="2xs" color="$textHint" italic>
        No task data
      </Text>
    );
  }

  // Calculate overall progress for accessibility
  const totalTasks = sortedLevels.reduce(
    (sum, lvl) => sum + levelProgress[lvl].total,
    0,
  );
  const completedTasks = sortedLevels.reduce(
    (sum, lvl) => sum + levelProgress[lvl].completed,
    0,
  );
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <HStack
      space={compact ? 'xs' : 'md'}
      flexWrap="wrap"
      alignItems="center"
      accessibilityLabel={`Task progress: ${completedTasks} of ${totalTasks} completed (${progressPercent}%)`}
    >
      {sortedLevels.map((level) => {
        const engagement = levelProgress[level];
        const states = engagement.taskStates || [];
        const taskIds = engagement.taskIds || [];

        return (
          <HStack key={level} space="xs" alignItems="center">
            {!compact && (
              <Text size="xs" color="$textSubtle" fontWeight="medium">
                L
                {level}
                :
              </Text>
            )}
            <HStack space="xs">
              {/* Task boxes are identified by their position within the level - stable keys */}
              {states.map((state, taskIndex) => {
                const taskState = state as TaskState;
                const color = STATE_COLORS[taskState] || STATE_COLORS.todo;
                const label = STATE_LABELS[taskState] || 'Unknown';
                // Using level + taskIndex as key is appropriate here because:
                // 1. Tasks within a level don't reorder
                // 2. The position IS the natural identifier for the task
                const taskKey = `level-${level}-task-${taskIndex}`;
                const taskId = taskIds[taskIndex];

                return (
                  <Tooltip
                    key={taskKey}
                    placement="top"
                    trigger={(triggerProps) => (
                      <Pressable
                        {...triggerProps}
                        onPress={() => taskId && onTaskPress?.(taskId)}
                        disabled={!taskId || !onTaskPress}
                      >
                        <Box
                          w={compact ? 8 : 12}
                          h={compact ? 8 : 12}
                          bg={color}
                          borderRadius="$xs"
                          borderWidth={1}
                          borderColor="$borderLight300"
                          testID={`task-box-${level}-${taskIndex}-${taskState}`}
                        />
                      </Pressable>
                    )}
                  >
                    <TooltipContent>
                      <TooltipText size="xs">
                        L
                        {level}
                        {' '}
                        Task
                        {' '}
                        {taskIndex + 1}
                        :
                        {' '}
                        {label}
                      </TooltipText>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </HStack>
          </HStack>
        );
      })}
    </HStack>
  );
};

/**
 * Calculates overall progress percentage from level progress data.
 */
export const calculateOverallProgress = (
  levelProgress: Record<string, LevelEngagement>,
): number => {
  const levels = Object.values(levelProgress);
  if (levels.length === 0) return 0;

  const total = levels.reduce((sum, l) => sum + l.total, 0);
  const completed = levels.reduce((sum, l) => sum + l.completed, 0);

  return total > 0 ? Math.round((completed / total) * 100) : 0;
};

/**
 * Legend component to explain the task box colors.
 */
export const TaskBoxesLegend: React.FC = () => (
  <HStack space="md" alignItems="center">
    <HStack space="xs" alignItems="center">
      <Box w={10} h={10} bg={STATE_COLORS.done} borderRadius="$xs" borderWidth={1} borderColor="$borderLight300" />
      <Text size="2xs" color="$textSubtle">Done</Text>
    </HStack>
    <HStack space="xs" alignItems="center">
      <Box w={10} h={10} bg={STATE_COLORS.started} borderRadius="$xs" borderWidth={1} borderColor="$borderLight300" />
      <Text size="2xs" color="$textSubtle">In Progress</Text>
    </HStack>
    <HStack space="xs" alignItems="center">
      <Box w={10} h={10} bg={STATE_COLORS.todo} borderRadius="$xs" borderWidth={1} borderColor="$borderLight300" />
      <Text size="2xs" color="$textSubtle">Not Started</Text>
    </HStack>
  </HStack>
);

export default TaskBoxes;
